import heroImage from '../assets/shiriki-hero-image.jpg';
import logo from '../assets/shiriki-logo.png';
import texamLogo from '../assets/texam-logo.png';
import './LandingPage.css';

export default function LandingPage() {
  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <img src={heroImage} alt="Books sharing community" className="hero-image" />
        <div className="hero-content">
          <img src={logo} alt="Shiriki Logo" className="hero-logo" />
          <h1 className="hero-headline">Share Books, Share Knowledge</h1>
          <p className="hero-subheadline">
            A community-driven platform for lending and borrowing physical books with nn Educational Books section as a social cause initiative designed to help students
            Discover, request, and share books with trust and transparency.
          </p>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="section welcome-section">
        <h2 className="section-title">Welcome to Shiriki</h2>
        <div className="welcome-content">
          <p>
            <strong>Shiriki</strong> is a social book-sharing platform where book lovers come together to lend, borrow, and circulate books within a trusted community. The concept is simple yet powerful: book owners list the books they own, creating a shared library that anyone can browse and borrow from.
          </p>
          <p>
            Whether you're looking for your next great read or want to share your collection with others, Shiriki connects readers and owners through a seamless request and fulfillment process. Browse the library, find a book you love, request it, and have it shipped directly to you.
          </p>
          <p>
            Beyond general reading, Shiriki has a dedicated <strong>Educational Books</strong> section—a social cause initiative designed to help students access textbooks they might not otherwise afford. By sharing and caring, we're making education more accessible, one book at a time.
          </p>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="section value-section">
        <h2 className="section-title">Why Choose Shiriki?</h2>
        <div className="value-grid">
          <div className="value-card">
            <div className="value-icon">📖</div>
            <h3>For Readers</h3>
            <ul>
              <li><strong>Access Thousands of Books</strong> – Borrow from local book lovers and students.</li>
              <li><strong>Save Money</strong> – Read without buying; only pay for shipping.</li>
              <li><strong>Educational Focus</strong> – Get textbooks for free or low cost as a social cause.</li>
            </ul>
          </div>
          <div className="value-card">
            <div className="value-icon">📚</div>
            <h3>For Book Owners</h3>
            <ul>
              <li><strong>Share Your Collection</strong> – Give your books a second life and connect with readers.</li>
              <li><strong>Earn Goodwill</strong> – Become a community hero by helping others learn.</li>
              <li><strong>Track Your Books</strong> – See where your books travel via the Book Journey timeline.</li>
            </ul>
          </div>
          <div className="value-card">
            <div className="value-icon">🎓</div>
            <h3>For Students & Educators</h3>
            <ul>
              <li><strong>Affordable Textbooks</strong> – Borrow academic books you can't afford to buy.</li>
              <li><strong>Donate Books</strong> – Contribute to educational equity by donating your old textbooks.</li>
              <li><strong>Support Learning</strong> – Join a movement that makes education accessible to all.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section how-it-works-section">
        <h2 className="section-title">How It Works</h2>
        <div className="steps-container">
          <div className="step">
            <div className="step-number">1</div>
            <h3>Browse & Discover</h3>
            <p>Switch between General Books and Educational Books. Use advanced filters to find exactly what you need.</p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <h3>Request a Book</h3>
            <p>Tap "Request" and provide your shipping details. The book owner will reply with shipping cost and payment details.</p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <h3>Book Shipped</h3>
            <p>When your request is accepted, The book will be shipped and delivered to you by the logistics partner.</p>
          </div>
          <div className="step">
            <div className="step-number">4</div>
            <h3>Read & Return</h3>
            <p>Enjoy reading! When done, initiate return and load the book back into the library for other readers to request.</p>
          </div>
          <div className="step">
            <div className="step-number">5</div>
            <h3>Track the Journey</h3>
            <p>Every book has a visual Book Journey Timeline showing its path from owner to reader and back.</p>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="section features-section">
        <h2 className="section-title">Key Features</h2>
        <div className="features-grid">
          <div className="feature-item">
            <span className="feature-icon">📚</span>
            <h4>Dual Library</h4>
            <p>General Books for leisure reading, Educational Books for academic needs.</p>
          </div>
          <div className="feature-item">
            <span className="feature-icon">🔍</span>
            <h4>Smart Discovery</h4>
            <p>Server-side filtering, search by title/author, and other filers for all Books.</p>
          </div>
          <div className="feature-item">
            <span className="feature-icon">📦</span>
            <h4>Seamless Logistics</h4>
            <p>Built-in logistics support and tracking via delivery partner.</p>
          </div>
          <div className="feature-item">
            <span className="feature-icon">🛡️</span>
            <h4>Safety & Trust</h4>
            <p>Verified user profiles, community guidelines, and report functionality.</p>
          </div>
        </div>
      </section>

      {/* Social Impact */}
      <section className="section impact-section">
        <h2 className="section-title">Social Impact</h2>
        <div className="impact-content">
          <p className="impact-intro">
            Shiriki's <strong>Educational Books</strong> category is a <strong>social cause</strong> aimed at solving the textbook affordability crisis.
          </p>
          <div className="impact-points">
            <div className="impact-point">
              <span className="impact-icon">🎒</span>
              <p><strong>For Students:</strong> Borrow required textbooks at no cost (or minimal shipping).</p>
            </div>
            <div className="impact-point">
              <span className="impact-icon">🤝</span>
              <p><strong>For Donors:</strong> Teachers, NGOs, and individuals can donate academic books.</p>
            </div>
            <div className="impact-point">
              <span className="impact-icon">🌍</span>
              <p><strong>For Society:</strong> Reduces paper waste and advances UN SDG 4 (Quality Education).</p>
            </div>
          </div>
          <blockquote className="impact-quote">
            "Shiriki isn't just about sharing books—it's about sharing opportunities."
          </blockquote>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section testimonials-section">
        <h2 className="section-title">What People Say</h2>
        <div className="testimonials-grid">
          <div className="testimonial-card">
            <p className="testimonial-text">
              "As a college student, I couldn't afford engineering textbooks. Shiriki connected me with a senior who lent me the entire set for the semester. Lifesaver!"
            </p>
            <p className="testimonial-author">– Rohan, Engineering Student</p>
          </div>
          <div className="testimonial-card">
            <p className="testimonial-text">
              "I've shared over 50 books from my personal library. Seeing the journey timeline of each book gives me joy—knowing my books are helping others learn."
            </p>
            <p className="testimonial-author">– Priya, Avid Reader</p>
          </div>
          <div className="testimonial-card">
            <p className="testimonial-text">
              "Our school partnered with Shiriki to create a textbook-sharing program. Now students from low-income families have access to the same resources as everyone else."
            </p>
            <p className="testimonial-author">– Mr. Sharma, Retired Teacher</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section" id="download">
        <h2 className="cta-title">Ready to Join the Sharing Revolution?</h2>
        <p className="cta-subtitle">Download the Shiriki App and start sharing books today.</p>
        <div className="cta-buttons">
          <a href="#" className="store-button">
            <span className="store-icon">🍎</span>
            <div className="store-text">
              <span className="store-label">Download on the</span>
              <span className="store-name">App Store</span>
            </div>
          </a>
          <a href="#" className="store-button">
            <span className="store-icon">▶️</span>
            <div className="store-text">
              <span className="store-label">Get it on</span>
              <span className="store-name">Google Play</span>
            </div>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-top">
          <div className="footer-brand">
            <img src={logo} alt="Shiriki" className="footer-logo" />
            <p className="footer-tagline">Share Books, Share Knowledge</p>
          </div>
          <div className="footer-sponsor">
            <img src={texamLogo} alt="Texam" className="sponsor-logo" />
            <p className="sponsor-credit">App development sponsor</p>
          </div>
          <div className="footer-links">
            <div className="footer-column">
              <h4>Platform</h4>
              <a href="#">About Shiriki</a>
              <a href="#">Download App</a>
            </div>
            <div className="footer-column">
              <h4>Resources</h4>
              <a href="#">Safety & Ethics</a>
              <a href="#">Community Guidelines</a>
            </div>
            <div className="footer-column">
              <h4>Contact</h4>
              <a href="mailto:hello@shiriki.app">hello@shiriki.in</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}